export type ButtonVariant =
  | "primary"
  | "secondary"
  | "secondary-card"
  | "outline"
  | "rounded-primary"
  | "rounded-secondary"
  | "cta"
  | "form"
  | "dialog"
  | "classic";

export class AppButton extends HTMLElement {
  constructor() {
    super();
  }

  // Se ejecuta automáticamente cuando el componente se inserta en el DOM
  connectedCallback() {
    this.render();
  }

  // Método para observar cambios en los atributos
  static get observedAttributes() {
    return [
      "text",
      "variant",
      "type",
      "disabled",
      "svg-icon",
      "svg-class",
      "svg-viewbox",
    ];
  }

  get disabled() {
    return this.hasAttribute("disabled");
  }

  set disabled(val) {
    if (val) {
      this.setAttribute("disabled", "");
    } else {
      this.removeAttribute("disabled");
    }
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (oldValue !== newValue) {
      this.render();
    }
  }

  render() {
    const text = this.getAttribute("text") || "Botón";
    const variant: ButtonVariant =
      (this.getAttribute("variant") as ButtonVariant) || "primary";
    const type = this.getAttribute("type") || "button";
    const isDisabled = this.disabled;
    const svgIconAttr = this.getAttribute("svg-icon");
    const svgClassAttr =
      this.getAttribute("svg-class") || "w-5 h-5 mr-2 shrink-0";
    const svgViewboxAttr = this.getAttribute("svg-viewbox") || "0 0 25 25";

    // Clases base de Tailwind — diseño pill (redondeado completo)
    let classes =
      "px-5 md:px-12 py-1 font-bold shadow-lg active:scale-[0.98] transition-all duration-150 flex items-center gap-4 justify-center whitespace-nowrap text-lg md:text-xl ";

    if (variant === "secondary-card") {
      classes +=
        "flex-col md:flex-row w-36 h-28 md:w-auto md:h-auto !px-3 md:!px-12 !py-2 md:!py-1 !gap-1 md:!gap-4 text-center md:text-left !text-base md:!text-xl leading-tight md:leading-normal rounded-2xl md:rounded-bl-2xl md:rounded-tr-2xl md:rounded-tl-none md:rounded-br-none rounded-lg bg-cream-light hover:bg-cream text-green-light !whitespace-normal md:!whitespace-nowrap !leading-none ";
    } else if (variant === "primary") {
      classes +=
        "rounded-bl-2xl rounded-tr-2xl bg-green-light hover:bg-green text-cream-light ";
    } else if (variant === "secondary") {
      classes +=
        "rounded-bl-2xl rounded-tr-2xl bg-cream-light hover:bg-cream text-green-light ";
    } else if (variant === "outline") {
      classes +=
        "bg-transparent border-2 border-white text-white hover:bg-white/10 ";
    } else if (variant === "cta") {
      classes +=
        "rounded-bl-2xl rounded-tr-2xl bg-green-light hover:bg-green text-cream-light text-xl md:text-2xl ";
    } else if (variant === "form") {
      classes += "form-pill ";
    } else if (variant === "dialog") {
      classes += "bg-green hover:bg-green/90 text-cream-light text-[1.2rem] rounded-lg";
    } else if (variant === "rounded-primary") {
      classes += "rounded-lg bg-green-light hover:bg-green text-cream-light ";
    } else if (variant === "rounded-secondary") {
      classes += "rounded-lg bg-cream-light hover:bg-cream text-green-light ";
    } else if (variant === "classic") {
        classes += "bg-green hover:bg-green/90 text-cream-light !py-0 md:!py-1 md:!px-3 ";
    }
    // Estado visual de deshabilitado manejado internamente
    if (isDisabled) {
      classes += "opacity-50 cursor-not-allowed active:scale-100 ";
    }

    let svgIcon = "";
    if (svgIconAttr) {
      const isUrl =
        svgIconAttr.startsWith("/") ||
        svgIconAttr.startsWith("http") ||
        svgIconAttr.endsWith(".svg");
      if (isUrl) {
        svgIcon = `<img src="${svgIconAttr}" class="${svgClassAttr}" alt="">`;
      } else {
        svgIcon = `<svg class="${svgClassAttr}" xmlns="http://www.w3.org/2000/svg" viewBox="${svgViewboxAttr}">${svgIconAttr}</svg>`;
      }
    }

    this.innerHTML = `
            <button type="${type}" class="${classes}" ${isDisabled ? "disabled" : ""}>
                ${svgIcon}${text}
            </button>
        `;
  }
}

// Registrar el Web Component en el navegador
if (typeof window !== "undefined") {
  customElements.define("app-button", AppButton);
}
